import { useRef, useState } from 'react'
import { clamp, clampRect } from '../../normalizedRect'
import { MIN_BBOX_PX } from './bboxSize'

export type HandleDirection =
  | 'move'
  | 'n'
  | 's'
  | 'e'
  | 'w'
  | 'ne'
  | 'nw'
  | 'se'
  | 'sw'

type RectNorm = { x: number; y: number; width: number; height: number }

type Setters = {
  setX: (x: number) => void
  setY: (y: number) => void
  setWidth: (w: number) => void
  setHeight: (h: number) => void
}

/**
 * 既存のBBoxを移動・リサイズする
 * - BBox本体ドラッグ: 移動
 * - 8方向ハンドルドラッグ: リサイズ（n/s/e/w/ne/nw/se/sw）
 */
export const useBBoxInteractions = (
  containerRef: React.RefObject<HTMLDivElement | null>,
  rect: RectNorm,
  setters: Setters,
) => {
  const [activeHandle, setActiveHandle] = useState<HandleDirection | null>(null)
  const pointerIdRef = useRef<number | null>(null)
  const dragStartRef = useRef<{ x: number; y: number } | null>(null)
  const initialRectRef = useRef<RectNorm | null>(null)

  const begin = (
    e: React.PointerEvent<HTMLElement>,
    handle: HandleDirection,
  ) => {
    if (!(rect.width > 0 && rect.height > 0)) return
    e.preventDefault()
    e.stopPropagation()
    const container = containerRef.current
    if (!container) return
    const c = container.getBoundingClientRect()
    pointerIdRef.current = e.pointerId
    setActiveHandle(handle)
    dragStartRef.current = { x: e.clientX - c.left, y: e.clientY - c.top }
    initialRectRef.current = { ...rect }
    container.setPointerCapture?.(e.pointerId)
  }

  const end = (_e?: React.PointerEvent<HTMLElement>) => {
    const pid = pointerIdRef.current
    if (pid != null) {
      const container = containerRef.current
      container?.releasePointerCapture?.(pid)
    }
    pointerIdRef.current = null
    setActiveHandle(null)
    dragStartRef.current = null
    initialRectRef.current = null
  }

  const moveWhileActive = (e: React.PointerEvent<HTMLElement>) => {
    const handle = activeHandle
    const dragStart = dragStartRef.current
    const initialRect = initialRectRef.current
    if (!handle || !dragStart || !initialRect) return
    const container = containerRef.current
    if (!container) return
    const c = container.getBoundingClientRect()
    const px = e.clientX - c.left
    const py = e.clientY - c.top

    const dx = (px - dragStart.x) / c.width
    const dy = (py - dragStart.y) / c.height

    const minWidthNorm = MIN_BBOX_PX / c.width
    const minHeightNorm = MIN_BBOX_PX / c.height

    if (handle === 'move') {
      const moved = clampRect({
        ...initialRect,
        x: initialRect.x + dx,
        y: initialRect.y + dy,
      })
      setters.setX(moved.x)
      setters.setY(moved.y)
      return
    }

    let left = initialRect.x
    let top = initialRect.y
    let right = initialRect.x + initialRect.width
    let bottom = initialRect.y + initialRect.height

    const currX = clamp(initialRect.x + dx, 0, 1)
    const currY = clamp(initialRect.y + dy, 0, 1)

    if (handle.includes('w')) {
      left = clamp(currX, 0, right - minWidthNorm)
    }
    if (handle.includes('e')) {
      right = clamp(
        initialRect.x + initialRect.width + dx,
        left + minWidthNorm,
        1,
      )
    }
    if (handle.includes('n')) {
      top = clamp(currY, 0, bottom - minHeightNorm)
    }
    if (handle.includes('s')) {
      bottom = clamp(
        initialRect.y + initialRect.height + dy,
        top + minHeightNorm,
        1,
      )
    }

    const resized = clampRect({
      x: left,
      y: top,
      width: clamp(right - left, minWidthNorm, 1),
      height: clamp(bottom - top, minHeightNorm, 1),
    })

    setters.setX(resized.x)
    setters.setY(resized.y)
    setters.setWidth(resized.width)
    setters.setHeight(resized.height)
  }

  return {
    activeHandle,
    begin,
    end,
    moveWhileActive,
  }
}
